import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  hideCustomerWelcomeMessage,
  modalCustomerWelcomeVisibleChanged,
  reRateByNopCommerCustomer,
  sendAllPetsFSCLead,
  setPowerUpErrors,
  validateAllPetsPowerups,
} from '../../actions';
import syncIcon from '../../assets/sync-clouds.svg';
import {
  forceFocusElement,
  validatePowerupsSelected,
} from '../../util';
import ModalContainer from './ModalContainer';
import './ModalCustomerWelcome.css';
import { useCallbackAfterRate } from '../../hooks';
import { AFTER_RATE_TAG } from '../../constants';

let isMounted = false;
const ModalCustomerWelcome = ({ goTo, isSecondStep = false }) => {
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const dispatch = useDispatch();
  const {
    data,
    guidData,
    isLoading,
    liveVetData,
    modalCustomerWelcomeVisible,
    parameters,
    petQuoteSelected,
    quoteSelectedValues,
    redirectCarryOver,
    sessionInformation,
    subId,
  } = store;
  const { nopCommerceUser, userEmail } = sessionInformation;
  const buttonRef = useRef(null);
  const { t } = useTranslation('quoting');
  const ESSENTIAL_PLAN = store.plans[0];
  const PREFERRED_PLAN = store.plans[1];
  const ULTIMATE_PLAN = store.plans[2];

  const isNewZipcode = useMemo(
    () => (parameters.petZipCode !== nopCommerceUser.Address?.ZipCode),
    [parameters.petZipCode, nopCommerceUser.Address],
  );

  useEffect(() => {
    if (!isMounted) {
      isMounted = true;
    }

    return () => dispatch(hideCustomerWelcomeMessage());
  }, [dispatch]);

  useEffect(() => {
    if (!isMounted || !nopCommerceUser.LoginSuccess) {
      return;
    }

    if (!redirectCarryOver
      && (sessionInformation.showWelcomeMessage || isNewZipcode)) {
      dispatch(modalCustomerWelcomeVisibleChanged(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isNewZipcode,
    nopCommerceUser.LoginSuccess,
    sessionInformation,
  ]);

  useEffect(() => {
    if (modalCustomerWelcomeVisible && !isLoading && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isLoading, modalCustomerWelcomeVisible]);

  function handleCloseFocus() {
    const quoteSelected = quoteSelectedValues
      .find((item) => item.quoteId === petQuoteSelected)
      || { petName: 'Pet Name' };

    switch (quoteSelected.selectedPlanIndex) {
      case ESSENTIAL_PLAN.index:
        forceFocusElement(`ESSENTIAL${ESSENTIAL_PLAN.index}`);
        break;

      case PREFERRED_PLAN.index:
        forceFocusElement(`PREFERRED${PREFERRED_PLAN.index}`);
        break;

      case ULTIMATE_PLAN.index:
        forceFocusElement(`ULTIMATE${ULTIMATE_PLAN.index}`);
        break;

      default:
        forceFocusElement(`PREFERRED${PREFERRED_PLAN.index}`);
        break;
    }
  }

  useCallbackAfterRate(() => {
    dispatch(sendAllPetsFSCLead());
    if (isSecondStep) {
      const petQuoteRateItem = data.ebPetQuoteResponseList
        .find((pqRateItem) => pqRateItem.petQuoteId === petQuoteSelected);
      const powerUpValidation = validatePowerupsSelected(petQuoteRateItem);
      if (!powerUpValidation.isValid) {
        dispatch(setPowerUpErrors(powerUpValidation.modifierIdNotSelectedList));

        window.scrollTo(0, 0);
      }
    }
  }, [AFTER_RATE_TAG.newZipCode]);

  const onClickGotIt = async () => {
    dispatch(modalCustomerWelcomeVisibleChanged(false));

    await dispatch(reRateByNopCommerCustomer({
      diamondClientId: nopCommerceUser.DiamonClientdId,
      ebGuID: guidData.GuID,
      ebPetQuoteResponseList: data.ebPetQuoteResponseList,
      effectiveDateCustom: store.effectiveDateCustom,
      eMail: userEmail || parameters.email,
      employerName: guidData.Name,
      groupCode: data.groupCode,
      groupCodeDscr: data.groupCodeDscr,
      nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
      subId: subId && subId.Name && subId.Value ? subId : {},
      zipCode: nopCommerceUser.Address.ZipCode,
    }));

    dispatch(validateAllPetsPowerups());

    handleCloseFocus();

    if (goTo && liveVetData) {
      goTo();
    }
  };

  function renderContent() {
    if (!isNewZipcode) {
      return (
        <p>
          {t('welcomeModal.currentZipcode')}
        </p>
      );
    }

    return (
      <p>
        {t('welcomeModal.newZipcode', {
          zipCode: nopCommerceUser.Address.ZipCode,
        })}
      </p>
    );
  }

  return (
    <ModalContainer
      cancelable={false}
      show={modalCustomerWelcomeVisible && !isLoading}
    >
      <div className="Flex Column Modal-customer-welcome-container">
        <img
          alt=""
          className="Modal-login-user-subscription-bubbles-image"
          src={syncIcon}
        />

        <h1 className="Modal-login-user-subscription-title">
          {t('welcomeModal.welcomeBack')}
        </h1>

        {renderContent()}

        <button
          ref={buttonRef}
          aria-label="Got it"
          className="Modal-customer-welcome-got-it"
          onClick={onClickGotIt}
          type="button"
        >
          <span className="Modal-customer-got-it-text">
            {t('welcomeModal.gotIt')}
          </span>
        </button>
      </div>
    </ModalContainer>

  );
};

export default ModalCustomerWelcome;
